/**
 * Pure-JS consent state management.
 * No React dependency — usable from main.tsx before React renders
 * and from React components via ConsentContext.
 */

export interface ConsentState {
  analytics: boolean;
  timestamp: string; // ISO 8601
  version: number;
}

/**
 * On-disk shape may carry additional legacy fields (e.g. `marketing` from
 * an earlier consent-gated pixel install). Read via getStoredConsent which
 * ignores unknown fields and only surfaces analytics consent.
 */
interface StoredConsentRecord {
  analytics?: boolean;
  marketing?: boolean; // legacy — ignored at read time
  timestamp?: string;
  version?: number;
}

export const CONSENT_STORAGE_KEY = 'coachsim-consent';
export const CONSENT_VERSION = 1;
export const CONSENT_EXPIRY_MONTHS = 12;

/** Average days per month for expiry calculation */
const AVG_DAYS_PER_MONTH = 30.44;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Read stored consent from localStorage.
 * Returns null if missing, expired (>12 months), or version mismatch.
 *
 * Legacy `marketing` field is ignored — the Meta Pixel is now installed
 * unconditionally in index.html and no longer gated by an in-app toggle.
 */
export function getStoredConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const stored: StoredConsentRecord = JSON.parse(raw);

    // Version mismatch — policy changed, re-prompt
    if (stored.version !== CONSENT_VERSION) return null;
    if (!stored.timestamp) return null;

    // Expiry check — 12 months from timestamp
    const expiryMs = CONSENT_EXPIRY_MONTHS * AVG_DAYS_PER_MONTH * MS_PER_DAY;
    const consentAge = Date.now() - new Date(stored.timestamp).getTime();
    if (consentAge > expiryMs) return null;

    return {
      analytics: stored.analytics === true,
      timestamp: stored.timestamp,
      version: stored.version,
    };
  } catch {
    return null;
  }
}

/** Persist consent state to localStorage. */
export function saveConsent(state: ConsentState): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing or quota — consent won't persist but app still works
  }
}

/** Remove stored consent. */
export function clearConsent(): void {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // no-op
  }
}

/** Convenience: returns true only if valid, non-expired consent with analytics=true exists. */
export function hasAnalyticsConsent(): boolean {
  const stored = getStoredConsent();
  return stored !== null && stored.analytics === true;
}

/** Build a new ConsentState with current timestamp and version. */
export function buildConsentState(analytics: boolean): ConsentState {
  return {
    analytics,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
}
